/**
 * Popup Content Generation
 *
 * Handles creating HTML content for feature popups
 */

import { PROPERTY_LABELS } from './config.js';

/**
 * Create popup HTML content from feature properties
 * @param {Object} properties - Feature properties
 * @param {string} county - County name
 * @returns {string} HTML content
 */
export function createPopupContent(properties, county) {
  // Filter out null/undefined values and internal properties
  const filteredProps = Object.entries(properties).filter(([key, value]) => {
    // Skip null, undefined, empty strings
    if (value === null || value === undefined || value === '') return false;

    // Skip internal properties
    if (key.startsWith('_') || key === 'fid') return false;

    return true;
  });

  // Build HTML table
  let html = '<div class="popup-content">';
  html += `<h3 style="margin: 0 0 10px 0; color: #333; font-size: 14px; border-bottom: 2px solid #0078A8; padding-bottom: 5px;">${county} County Parcel</h3>`;
  html += '<table class="popup-table" style="width: 100%; font-size: 12px; border-collapse: collapse;">';

  filteredProps.forEach(([key, value]) => {
    const label = PROPERTY_LABELS[key] || formatPropertyName(key);
    const formattedValue = formatPropertyValue(key, value);

    html += `
      <tr>
        <td style="padding: 4px 8px; font-weight: bold; color: #555; border-bottom: 1px solid #eee; vertical-align: top;">
          ${label}:
        </td>
        <td style="padding: 4px 8px; color: #333; border-bottom: 1px solid #eee; word-break: break-word;">
          ${formattedValue}
        </td>
      </tr>
    `;
  });

  html += '</table>';
  html += '</div>';

  return html;
}

/**
 * Format property name for display
 * Converts snake_case and SCREAMING_SNAKE_CASE to Title Case
 * @param {string} name - Property name
 * @returns {string} Formatted name
 */
function formatPropertyName(name) {
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Format property value for display
 * @param {string} key - Property key
 * @param {*} value - Property value
 * @returns {string} Formatted value
 */
function formatPropertyValue(key, value) {
  // Handle different value types
  if (typeof value === 'number') {
    // Format currency fields
    if (key.includes('Value') || key.includes('VAL') || key.includes('TOTAL')) {
      return formatCurrency(value);
    }

    // Format acreage
    if (key.includes('ACRE') || key === 'ACRES') {
      return value.toFixed(3) + ' acres';
    }

    // Format square footage
    if (key.includes('SQ_FT') || key.includes('SQFT')) {
      return value.toLocaleString() + ' sq ft';
    }

    // Format other numbers
    return value.toLocaleString();
  }

  // Handle URLs
  if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) {
    return `<a href="${value}" target="_blank" rel="noopener noreferrer">Link</a>`;
  }

  // Handle long text (legal descriptions, etc.)
  if (typeof value === 'string' && value.length > 100) {
    return `<div style="max-height: 100px; overflow-y: auto;">${escapeHtml(value)}</div>`;
  }

  // Default: return as string
  return escapeHtml(String(value));
}

/**
 * Format number as currency
 * @param {number} value - Numeric value
 * @returns {string} Formatted currency
 */
function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

/**
 * Escape HTML to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Create a compact popup with only key information
 * @param {Object} properties - Feature properties
 * @param {string} county - County name
 * @returns {string} HTML content
 */
export function createCompactPopup(properties, county) {
  // Key fields to show in compact view
  const keyFields = ['NAME', 'Owner_Name', 'file_as_na', 'SITUS', 'Situs', 'situs_disp', 'ACRES', 'ACREAGE', 'VAL25TOT', 'Total_Valu'];

  let html = '<div class="popup-content-compact">';
  html += `<h3 style="margin: 0 0 8px 0; color: #333; font-size: 13px;">${county} County</h3>`;

  keyFields.forEach(field => {
    if (properties[field] && properties[field] !== null) {
      const label = PROPERTY_LABELS[field] || formatPropertyName(field);
      const value = formatPropertyValue(field, properties[field]);
      html += `<div style="margin-bottom: 4px; font-size: 11px;"><strong>${label}:</strong> ${value}</div>`;
    }
  });

  html += '<div style="margin-top: 8px; font-size: 10px; color: #666;">Click for full details</div>';
  html += '</div>';

  return html;
}

/**
 * Create popup HTML content for subdivision/development features
 * @param {Object} properties - Feature properties
 * @param {string} type - Layer type ('subdivisions' or 'projects')
 * @returns {string} HTML content
 */
export function createSubdivisionPopup(properties, type) {
  const isSubdivision = type === 'subdivisions';
  const title = isSubdivision ? 'Future Subdivision' : 'Upcoming Project';
  const headerColor = isSubdivision ? '#ff8c00' : '#32cd32';

  let html = '<div class="subdivision-popup-content">';
  html += `<h3 style="margin: 0 0 10px 0; color: ${headerColor}; font-size: 15px; border-bottom: 2px solid ${headerColor}; padding-bottom: 5px;">${title}</h3>`;

  // Development name (prominent)
  if (properties.Name) {
    html += `<div style="font-size: 14px; font-weight: bold; margin-bottom: 10px; color: #333;">${escapeHtml(properties.Name)}</div>`;
  }

  // Key information section
  html += '<table class="popup-table" style="width: 100%; font-size: 12px; border-collapse: collapse;">';

  // Builder (if available)
  if (properties.Builder && properties.Builder.trim() !== '') {
    html += createPopupRow('Builder', properties.Builder);
  }

  // Location (City for projects, Section for subdivisions)
  if (properties.City) {
    html += createPopupRow('City', properties.City);
  } else if (properties.Section) {
    html += createPopupRow('Section', properties.Section);
  }

  // Status
  if (properties.Status) {
    html += createPopupRow('Status', `<span style="color: #0078A8; font-weight: bold;">${properties.Status}</span>`);
  }

  // Pricing section (highlighted)
  const priceMin = properties['Price Min'] || properties['Min Price'];
  const priceMax = properties['Price Max'] || properties['Max Price'];

  if (priceMin || priceMax) {
    html += `<tr><td colspan="2" style="padding-top: 10px; padding-bottom: 5px; font-weight: bold; color: #555; border-top: 1px solid #ddd;">Pricing</td></tr>`;

    if (priceMin && priceMax) {
      const formattedMin = formatCurrency(priceMin);
      const formattedMax = formatCurrency(priceMax);
      html += createPopupRow('Price Range', `${formattedMin} - ${formattedMax}`);
    } else if (priceMin) {
      html += createPopupRow('Starting Price', formatCurrency(priceMin));
    } else if (priceMax) {
      html += createPopupRow('Max Price', formatCurrency(priceMax));
    }
  }

  // Units section (highlighted)
  const unitsPlanned = properties['Units Planned'] || properties['Total Planned'];
  const unitsClosed = properties['Units Closed'] || properties['Total Sold'];

  if (unitsPlanned || unitsClosed) {
    html += `<tr><td colspan="2" style="padding-top: 10px; padding-bottom: 5px; font-weight: bold; color: #555; border-top: 1px solid #ddd;">Units</td></tr>`;

    if (unitsPlanned) {
      html += createPopupRow('Planned', `<strong>${unitsPlanned.toLocaleString()}</strong> units`);
    }
    if (unitsClosed) {
      html += createPopupRow('Sold', `<strong style="color: #32cd32;">${unitsClosed.toLocaleString()}</strong> units`);
    }
    if (unitsPlanned && unitsClosed) {
      const remaining = unitsPlanned - unitsClosed;
      if (remaining > 0) {
        html += createPopupRow('Remaining', `<strong style="color: #ff8c00;">${remaining.toLocaleString()}</strong> units`);
      }
    }
  }

  // Additional details
  const lotWidth = properties['Lot Width'] || properties['Lot Size'];
  const unitSizeMin = properties['Unit Size Min'] || properties['Min Unit Size'];
  const unitSizeMax = properties['Unit Size Max'] || properties['Max Unit Size'];

  if (lotWidth || unitSizeMin || unitSizeMax) {
    html += `<tr><td colspan="2" style="padding-top: 10px; padding-bottom: 5px; font-weight: bold; color: #555; border-top: 1px solid #ddd;">Property Details</td></tr>`;

    if (lotWidth && lotWidth > 0) {
      html += createPopupRow('Lot Width', `${lotWidth} ft`);
    }
    if (unitSizeMin && unitSizeMax) {
      html += createPopupRow('Unit Size', `${unitSizeMin.toLocaleString()} - ${unitSizeMax.toLocaleString()} sq ft`);
    } else if (unitSizeMin) {
      html += createPopupRow('Unit Size', `${unitSizeMin.toLocaleString()} sq ft`);
    } else if (unitSizeMax) {
      html += createPopupRow('Max Unit Size', `${unitSizeMax.toLocaleString()} sq ft`);
    }
  }

  // Product style
  if (properties['Product Style']) {
    html += createPopupRow('Product Style', properties['Product Style']);
  }

  html += '</table>';
  html += '</div>';

  return html;
}

/**
 * Helper function to create a popup table row
 * @param {string} label - The label text
 * @param {string} value - The value text (can include HTML)
 * @returns {string} HTML table row
 */
function createPopupRow(label, value) {
  return `
    <tr>
      <td style="padding: 4px 8px; font-weight: bold; color: #555; border-bottom: 1px solid #eee; vertical-align: top; white-space: nowrap;">
        ${label}:
      </td>
      <td style="padding: 4px 8px; color: #333; border-bottom: 1px solid #eee; word-break: break-word;">
        ${value}
      </td>
    </tr>
  `;
}
